import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Users,
  Plus,
  Search,
  RefreshCw,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Upload,
} from "lucide-react";
import type { StudentListItem } from "./types";
import StudentForm from "./StudentForm";
import { toastMessage } from "@/utils/toast-message";
import { apiRequest } from "@/utils/api-request";
import { erpPath } from "@/utils/erp-host";
import { titleCase } from "@/utils/text";
import { hasPaPermission } from "@/utils/pa-permissions";
import MultiClassSelect, { CurriculumGroup, ClassFilterChips } from "@/components/common/MultiClassSelect";

interface Section {
  id: number;
  section_label: string;
}

const StudentsManagement: React.FC = () => {
  // Filters are backed by URL query params so that returning from a student's
  // full-page profile (Back) restores the exact filtered view.
  const [searchParams, setSearchParams] = useSearchParams();

  const [students, setStudents] = useState<StudentListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(() => Number(searchParams.get("page")) || 1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState(() => searchParams.get("search") || "");
  const [selectedClasses, setSelectedClasses] = useState<string[]>(() =>
    searchParams.get("class_id")?.split(",").filter(Boolean) ?? []
  );
  const [filterSection, setFilterSection] = useState(() => searchParams.get("section_id") || "");
  const [classGroups, setClassGroups] = useState<CurriculumGroup[]>([]);
  const [sections, setSections] = useState<Section[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editStudent, setEditStudent] = useState<{ uuid: string } | null>(null);
  const navigate = useNavigate();

  const limit = 20;
  const hasFilter = !!(search || selectedClasses.length > 0);

  // Keep the URL in sync with the active filters (replace, so we don't spam
  // browser history while typing/paging).
  useEffect(() => {
    const next: Record<string, string> = {};
    if (search) next.search = search;
    if (selectedClasses.length) next.class_id = selectedClasses.join(",");
    if (filterSection && selectedClasses.length === 1) next.section_id = filterSection;
    if (page > 1) next.page = String(page);
    setSearchParams(next, { replace: true });
  }, [search, selectedClasses, filterSection, page, setSearchParams]);

  useEffect(() => {
    apiRequest<any>("/api/parent-app/admin/classes").then((res) => {
      setClassGroups(res?.data?.grouped ?? []);
    });
  }, []);

  useEffect(() => {
    if (selectedClasses.length !== 1) {
      setSections([]);
      setFilterSection("");
      return;
    }
    apiRequest<any>(`/api/parent-app/admin/classes/${selectedClasses[0]}/sections`).then((res) => {
      const list = res?.data?.data ?? [];
      setSections(Array.isArray(list) ? list : []);
    });
  }, [selectedClasses]);

  const fetchStudents = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (search) qs.set("search", search);
      selectedClasses.forEach(id => qs.append("class_id", id));
      if (filterSection && selectedClasses.length === 1) qs.set("section_id", filterSection);

      const res = await apiRequest<any>(`/api/parent-app/admin/students?${qs}`);
      if (res.success && res.data) {
        setStudents(res.data.data);
        setTotal(res.data.total);
      }
    } catch {
      toastMessage("Failed to load students", "error");
    } finally {
      setLoading(false);
    }
  }, [page, search, selectedClasses, filterSection]);

  useEffect(() => {
    if (!hasFilter) {
      setStudents([]);
      setTotal(0);
      return;
    }
    fetchStudents();
  }, [page, search, selectedClasses, filterSection]);

  const totalPages = Math.ceil(total / limit);

  return (
    <>
      {/* Page Head */}
      <div className="page-head">
        <div className="page-head-icon">
          <Users size={24} />
        </div>
        <div className="page-head-text">
          <h1>Students</h1>
          <p>
            {hasFilter && total > 0
              ? `${total} found`
              : "Search or filter to load students"}
          </p>
        </div>
        <div className="page-head-actions">
          <div style={{ position: "relative" }}>
            <Search
              size={14}
              style={{
                position: "absolute",
                left: 12,
                top: "50%",
                transform: "translateY(-50%)",
                color: "var(--text-3)",
                pointerEvents: "none",
              }}
            />
            <input
              type="text"
              placeholder="Search by name or admission no."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="input"
              style={{ paddingLeft: 36, width: 260 }}
            />
          </div>
          <MultiClassSelect
            classGroups={classGroups}
            selectedIds={selectedClasses}
            onChange={(ids) => { setSelectedClasses(ids); setFilterSection(""); setPage(1); }}
          />
          {selectedClasses.length === 1 && sections.length > 0 && (
            <select
              value={filterSection}
              onChange={(e) => {
                setFilterSection(e.target.value);
                setPage(1);
              }}
              className="select"
              style={{ width: "auto" }}
            >
              <option value="">All Sections</option>
              {sections.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.section_label}
                </option>
              ))}
            </select>
          )}
          {hasFilter && (
            <button
              onClick={fetchStudents}
              className="icon-btn"
              title="Refresh"
            >
              <RefreshCw size={18} />
            </button>
          )}
          {hasPaPermission('students', 'import') && (
            <button
              onClick={() => navigate(erpPath("bulk-import?module=students"))}
              className="btn btn-secondary"
            >
              <Upload size={17} />
              Import
            </button>
          )}
          <button
            onClick={() => {
              setEditStudent(null);
              setShowForm(true);
            }}
            className="btn btn-primary"
          >
            <Plus size={17} />
            Add Student
          </button>
        </div>
      </div>

      <ClassFilterChips classGroups={classGroups} selectedIds={selectedClasses} onChange={(ids) => { setSelectedClasses(ids); setPage(1); }} style={{ paddingLeft: 32, paddingRight: 32 }} />

      {/* Page Body */}
      <div className="page-body">
        {/* Content */}
        {loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 200,
            }}
          >
            <Loader2
              size={28}
              style={{
                animation: "spin 1s linear infinite",
                color: "var(--slate)",
              }}
            />
          </div>
        ) : !hasFilter ? (
          <div className="empty">
            <div className="empty-art">
              <Users size={44} />
            </div>
            <h3>No filter applied</h3>
            <p>Select a class or use search / status filter to load students.</p>
          </div>
        ) : students.length === 0 ? (
          <div className="empty">
            <div className="empty-art">
              <Users size={44} />
            </div>
            <h3>No students found</h3>
            <p>Try adjusting your filters.</p>
          </div>
        ) : (
          <div className="card" style={{ overflow: "hidden" }}>
            <div style={{ overflow: "auto", maxHeight: "520px" }}>
              <table className="tbl tbl-sticky">
                <thead>
                  <tr>
                    <th style={{ width: "26%" }}>Student</th>
                    <th>Admission No.</th>
                    <th>Class</th>
                    <th>Guardian</th>
                    <th style={{ width: 80 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((student) => {
                    const primaryContact = student.contacts?.[0];
                    return (
                      <tr
                        key={student.id}
                        onClick={() => navigate(erpPath(`students/${student.uuid}`))}
                        style={{ cursor: "pointer" }}
                      >
                        <td>
                          <div className="row gap-12">
                            {student.photo_url ? (
                              <img
                                src={student.photo_url}
                                alt=""
                                className="avatar"
                                style={{ width: 38, height: 38 }}
                              />
                            ) : (
                              <div
                                className="avatar cat-blue"
                                style={{
                                  width: 38,
                                  height: 38,
                                  fontSize: 14,
                                  fontWeight: 700,
                                }}
                              >
                                {student.display_name.charAt(0).toUpperCase()}
                              </div>
                            )}
                            <span
                              style={{
                                fontWeight: 700,
                                letterSpacing: "-0.2px",
                              }}
                            >
                              {titleCase(student.display_name)}
                            </span>
                          </div>
                        </td>
                        <td className="muted">
                          {student.admission_number ?? "—"}
                        </td>
                        <td>
                          <span className="chip chip-sm">
                            {student.class?.class_label ?? "—"}
                            {student.section
                              ? ` · ${student.section.section_label}`
                              : ""}
                          </span>
                        </td>
                        <td className="muted" style={{ fontSize: 13.5 }}>
                          {primaryContact ? (
                            <div>
                              <div style={{ fontWeight: 600, fontSize: 13 }}>
                                {titleCase(primaryContact.full_name)}
                              </div>
                              <div className="meta" style={{ marginTop: 2 }}>
                                {primaryContact.phone_primary}
                              </div>
                            </div>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditStudent({ uuid: student.uuid });
                              setShowForm(true);
                            }}
                            className="btn btn-ghost btn-sm"
                            style={{ color: "var(--info)" }}
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pagination */}
        {hasFilter && students.length > 0 && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 16,
              marginTop: 20,
            }}
          >
            <span className="meta">
              Showing {(page - 1) * limit + 1}–{Math.min(page * limit, total)}{" "}
              of {total} students
            </span>
            {totalPages > 1 && (
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="icon-btn"
                  style={{ opacity: page === 1 ? 0.5 : 1 }}
                >
                  <ChevronLeft size={18} />
                </button>
                <span
                  className="meta"
                  style={{ padding: "0 12px", fontSize: 12.5 }}
                >
                  {page} / {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="icon-btn"
                  style={{ opacity: page === totalPages ? 0.5 : 1 }}
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <StudentForm
          student={editStudent}
          onClose={() => setShowForm(false)}
          onSaved={() => {
            setShowForm(false);
            if (hasFilter) fetchStudents();
          }}
        />
      )}
    </>
  );
};

export default StudentsManagement;
